/**
 * Shared building blocks of the direction C « Flux » interface (validated by
 * Pascal on 26/09/2026). Every redesigned screen composes these instead of
 * inventing its own card, chip or drawer, so the product reads as one system.
 * Styles live in src/app/theme-c.css (section « Composants C »).
 */
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { withProviderLogo } from './ProviderLogo';

export type Tone = 'default' | 'pipeline' | 'signed' | 'produced' | 'billed' | 'paid' | 'alert' | 'error' | 'muted';

/* ── Segmented control: tabs that swap content in place ── */
export interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<SegmentOption<T>>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="c-segmented" role="tablist" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          className={`c-segmented__tab${option.value === value ? ' is-active' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* ── Stat card: one fact, one label, one detail line ── */
export function StatCard({
  label,
  value,
  detail,
  tone = 'default',
  to,
  onClick,
  children,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: Tone;
  /** Route of THE item this figure opens (never a generic list). */
  to?: string;
  onClick?: () => void;
  children?: ReactNode;
}) {
  const body = (
    <>
      <span className="c-stat__label">{withProviderLogo(label)}</span>
      <span className="c-stat__value num">{value}</span>
      {detail ? <span className="c-stat__detail">{detail}</span> : null}
      {children}
    </>
  );
  const className = `c-stat c-stat--${tone}`;
  if (to) return <Link to={to} className={`${className} is-interactive`}>{body}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={`${className} is-interactive`}>{body}</button>;
  return <div className={className}>{body}</div>;
}

/* ── Chips ── */
export function Chip({ tone = 'muted', children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return <span className={`c-chip c-chip--${tone}`} title={title}>{withProviderLogo(children)}</span>;
}

export type SourceState = 'ok' | 'warn' | 'error' | 'unknown';

/**
 * Freshness of one source in a single pill; the detail (never a full-width
 * banner) sits behind an ⓘ disclosure.
 */
export function SourceChip({ state, children, detail }: { state: SourceState; children: ReactNode; detail?: ReactNode }) {
  const tone: Tone = state === 'ok' ? 'paid' : state === 'warn' ? 'billed' : state === 'error' ? 'error' : 'muted';
  if (!detail) {
    return (
      <span className={`c-source c-chip c-chip--${tone}`}>
        <span className="c-source__dot" aria-hidden="true" />
        {withProviderLogo(children)}
      </span>
    );
  }
  return (
    <details className="c-source-detail">
      <summary className={`c-source c-chip c-chip--${tone}`}>
        <span className="c-source__dot" aria-hidden="true" />
        {withProviderLogo(children)}
        <span className="c-source__info" aria-hidden="true">i</span>
      </summary>
      <div className="c-popover" role="note">{detail}</div>
    </details>
  );
}

/* ── Info disclosure (ⓘ) for explanations that must not sit in the page ── */
export function InfoHint({ label, children }: { label: string; children: ReactNode }) {
  return (
    <details className="c-info">
      <summary aria-label={label} className="c-info__trigger">i</summary>
      <div className="c-popover" role="note">{children}</div>
    </details>
  );
}

/* ── Progress bar (share of a whole) ── */
export function ProgressBar({ value, tone = 'signed', label }: { value: number; tone?: Tone; label?: string }) {
  const pct = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0)) * 100;
  return (
    <span className="c-progress" role={label ? 'img' : undefined} aria-label={label}>
      <span className={`c-progress__fill c-progress__fill--${tone}`} style={{ width: `${pct}%` }} />
    </span>
  );
}

/* ── Honest state: what is missing and what to click next ── */
export function HonestState({
  title,
  detail,
  action,
  providerLogo = true,
}: {
  title: ReactNode;
  detail?: ReactNode;
  action?: ReactNode;
  /**
   * A title naming HubSpot or Qonto gets its logo (EXIGENCES.md §11 point 13).
   * `false` when the title is a value to complete (« Montant à renseigner dans
   * HubSpot ») or sits in a card whose source tag already carries the logo.
   */
  providerLogo?: boolean;
}) {
  return (
    <div className="c-honest">
      <span className="c-honest__title">{providerLogo ? withProviderLogo(title) : title}</span>
      {detail ? <span className="c-honest__detail">{detail}</span> : null}
      {action ? <span className="c-honest__action">{action}</span> : null}
    </div>
  );
}

/* ── Card with a title row ── */
export function Panel({
  title,
  aside,
  children,
  className = '',
  bodyClassName = '',
  providerLogo = true,
}: {
  title?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  /**
   * A string title or aside naming HubSpot or Qonto gets its logo (a source
   * tag « Qonto »). `false` when the aside is an explanatory sentence (« Sans
   * prochaine action dans HubSpot · plus gros montants d’abord »): no logo in
   * a sentence (EXIGENCES.md §11 point 13).
   */
  providerLogo?: boolean;
}) {
  const label = (node: ReactNode, size?: 'md') => (providerLogo ? withProviderLogo(node, size) : node);
  return (
    <section className={`c-card ${className}`.trim()}>
      {title || aside ? (
        <header className="c-card__head">
          {title ? <h2 className="c-card__title">{label(title, 'md')}</h2> : <span />}
          {aside ? <div className="c-card__aside">{label(aside)}</div> : null}
        </header>
      ) : null}
      <div className={`c-card__body ${bodyClassName}`.trim()}>{children}</div>
    </section>
  );
}

/* ── Docked detail panel (right side, the list stays visible) ── */
export function DetailPanel({
  id,
  label,
  eyebrow,
  title,
  subtitle,
  onClose,
  actions,
  children,
}: {
  /** DOM id, so list rows can point at the panel with aria-controls. */
  id?: string;
  /** Accessible name of the panel, e.g. « Détail de la mission ». */
  label: string;
  /** Kind of record and its source (« Affaire HubSpot · … »): a string naming HubSpot or Qonto gets its logo. */
  eyebrow?: ReactNode;
  /** The record's name: no automatic provider logo (opt in with <ProviderName>). */
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  /** Sibling action buttons, pinned at the bottom. */
  actions?: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <aside id={id} className="c-panel" aria-label={label}>
      <header className="c-panel__head">
        <div className="c-panel__identity">
          {eyebrow ? <span className="c-panel__eyebrow">{withProviderLogo(eyebrow)}</span> : null}
          {/* The title is the record's own name (a deal « Formation HubSpot »):
              never an automatic logo; the eyebrow carries the source. */}
          <h2 className="c-panel__title">{title}</h2>
          {subtitle ? <div className="c-panel__subtitle">{subtitle}</div> : null}
        </div>
        <button type="button" className="c-icon-button" onClick={onClose} aria-label="Fermer le détail">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>
      <div className="c-panel__body">{children}</div>
      {actions ? <footer className="c-panel__actions">{actions}</footer> : null}
    </aside>
  );
}

export function DetailSection({ title, aside, children }: { title: ReactNode; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="c-panel__section">
      <div className="c-panel__section-head">
        <h3>{withProviderLogo(title)}</h3>
        {aside ? <span className="c-panel__section-aside">{withProviderLogo(aside)}</span> : null}
      </div>
      {children}
    </section>
  );
}

/** Figures row inside a panel: two or three small facts. */
export function PanelFigures({ items }: { items: Array<{ label: string; value: ReactNode; tone?: Tone }> }) {
  return (
    <div className="c-panel__figures">
      {items.map((item) => (
        <div key={item.label} className={`c-panel__figure c-panel__figure--${item.tone || 'default'}`}>
          <span className="c-panel__figure-label">{withProviderLogo(item.label)}</span>
          <span className="c-panel__figure-value num">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

/** Split view: work area on the left, the docked panel on the right when open. */
export function SplitView({ panel, children }: { panel?: ReactNode; children: ReactNode }) {
  return (
    <div className={`c-split${panel ? ' c-split--open' : ''}`}>
      <div className="c-split__main">{children}</div>
      {panel || null}
    </div>
  );
}

/* ── Page title row (visible H1 + subtitle + actions) ── */
export function PageTitle({
  title,
  subtitle,
  controls,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  /** Inline controls next to the title (segmented control, period navigation). */
  controls?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="c-page-title">
      <div className="c-page-title__identity">
        <div className="c-page-title__row">
          <h1>{title}</h1>
          {controls}
        </div>
        {subtitle ? <div className="c-page-title__subtitle">{subtitle}</div> : null}
      </div>
      {actions ? <div className="c-page-title__actions">{actions}</div> : null}
    </header>
  );
}
export { ColumnResizeHandle, Th, clampColumnWidth, gridTemplate, useColumnWidths, useListColumns } from './ColumnResize';
export { ClientLogo, ClientName, ClientTitleLogo, clientInitials, clientLogoSrc, clientTint, type ClientLogoSize } from './ClientLogo';
export {
  INTERVENANT_AVATAR_PX,
  IntervenantAvatar,
  IntervenantDirectoryProvider,
  IntervenantName,
  buildIntervenantDirectory,
  useIntervenant,
  useIntervenantDirectory,
  type IntervenantAvatarSize,
  type IntervenantDirectory,
  type IntervenantMatch,
  type IntervenantRef,
} from './IntervenantName';
export { PROVIDER_NAME, ProviderLogo, ProviderName, providerOf, providersOf, withProviderLogo, type Provider, type ProviderLogoSize } from './ProviderLogo';
